import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蘑菇影视',
    url: 'https://mogutv.com/',
    description: '蘑菇影视聚集高清、超清、4K、蓝光画质影视资源，免费在线观看，无广告干扰，播放流畅不缓冲。',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@mogutv.com',
      telephone: '400-888-8888',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '蘑菇影视是免费的吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，蘑菇影视所有影视内容完全免费，无需充值会员，无隐藏费用，随时随地免费观看高清电影和电视剧。',
        },
      },
      {
        '@type': 'Question',
        name: '蘑菇影视支持哪些画质？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视支持标清、高清、超清、4K和蓝光等多种画质选择，根据您的网络状况和设备能力自动适配最佳画质，确保流畅观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '观看时会有广告吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不会。蘑菇影视承诺全程无广告干扰，观影过程不受任何广告打断，让您沉浸在纯粹的视听享受中。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视支持手机、平板、电脑和智能电视等多种设备观看，只需打开浏览器即可享受高清观影体验，无需下载任何App。',
        },
      },
      {
        '@type': 'Question',
        name: '影片资源更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '蘑菇影视每天更新海量影视资源，包括最新上映的电影、热播电视剧、人气动漫和综艺节目，确保您不错过任何精彩内容。',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <section className="mt-6">
              <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[360px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theater%20with%20warm%20golden%20stage%20curtains%20and%20soft%20spotlight%2C%20elegant%20classic%20movie%20palace%20interior%2C%20premium%20velvet%20seats%2C%20atmospheric%20lighting%20with%20warm%20tones%2C%20luxurious%20and%20nostalgic%20ambience%2C%20no%20people&width=1400&height=600&seq=about-hero-2026&orientation=landscape"
                  alt="蘑菇影视品牌"
                  title="蘑菇影视 - 品牌形象"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/40"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                  <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">
                    关于蘑菇影视
                  </h1>
                  <p className="text-white/80 text-sm md:text-base max-w-xl">
                    致力于为广大影迷提供最优质的高清观影体验
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                    <p>
                      <strong>蘑菇影视</strong>成立于2020年，是一家专注于高清影视内容聚合与分发的在线平台。我们致力于为广大影迷用户提供<strong>高清、超清、4K乃至蓝光画质</strong>的影视资源，涵盖电影、电视剧、动漫、综艺、纪录片等多种内容类型。
                    </p>
                    <p>
                      平台自上线以来，始终坚持<strong>"免费观影、无广告干扰、播放流畅不缓冲"</strong>的核心理念。我们深知用户对于观影体验的追求，因此在技术架构上不断优化，确保每一位用户都能享受到极致的视听盛宴。
                    </p>
                    <p>
                      蘑菇影视团队由一群热爱电影的年轻人组成，我们相信<strong>好电影值得被更多人看到</strong>。无论是院线大片还是经典老片，无论是国产佳作还是海外精品，我们都精心筛选，为用户呈现最优质的内容。
                    </p>
                    <p>
                      截至目前，蘑菇影视已累计服务<strong>超过5000万用户</strong>，日均播放量突破<strong>2000万次</strong>。我们不断拓展内容库，每日更新海量资源，确保用户始终能够找到自己想看的影视内容。
                    </p>
                  </div>
                </div>
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="font-semibold text-foreground-900 mb-4">平台数据</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-accent-500">5000万</span>
                        <span className="text-xs text-foreground-500">+</span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">累计服务用户</p>
                    </div>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-accent-500">2000万</span>
                        <span className="text-xs text-foreground-500">+</span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">日均播放次数</p>
                    </div>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-accent-500">10万</span>
                        <span className="text-xs text-foreground-500">+</span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">影视资源数量</p>
                    </div>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-accent-500">4.9</span>
                        <span className="text-xs text-foreground-500">/5.0</span>
                      </div>
                      <p className="text-xs text-foreground-500 mt-1">用户平均评分</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                品牌口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {[
                  {
                    name: '王先生',
                    role: '资深影迷',
                    content: '蘑菇影视真是我见过最好用的观影平台！画面清晰度没得说，关键是完全没有广告打扰，追剧体验一级棒。现在每天下班回家第一件事就是打开蘑菇影视。',
                  },
                  {
                    name: '李女士',
                    role: '全职妈妈',
                    content: '自从发现了蘑菇影视，我们全家周末的观影时光变得更加愉快了。资源丰富、更新快，孩子喜欢的动画片和大人的电影都能找到，而且全部免费！',
                  },
                  {
                    name: '张同学',
                    role: '大学生',
                    content: '作为一个电影爱好者，蘑菇影视满足了我对画质的所有要求。4K蓝光资源随便看，加载速度还特别快，宿舍网络也能流畅播放。强烈推荐！',
                  },
                  {
                    name: '刘总监',
                    role: 'IT从业者',
                    content: '从技术角度来说，蘑菇影视的流媒体优化做得很出色。在不同网络环境下都能智能适配画质，保证流畅播放。这是我愿意长期使用的重要原因。',
                  },
                ].map((review, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/70"
                  >
                    <div className="flex items-start gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-sm text-accent-500"></i>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{review.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-primary-600">
                          {review.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: '蘑菇影视是免费的吗？',
                    a: '是的，蘑菇影视所有影视内容完全免费，无需充值会员，无隐藏费用，随时随地免费观看高清电影和电视剧。',
                  },
                  {
                    q: '蘑菇影视支持哪些画质？',
                    a: '蘑菇影视支持标清、高清、超清、4K和蓝光等多种画质选择，根据您的网络状况和设备能力自动适配最佳画质，确保流畅观影体验。',
                  },
                  {
                    q: '观看时会有广告吗？',
                    a: '不会。蘑菇影视承诺全程无广告干扰，观影过程不受任何广告打断，让您沉浸在纯粹的视听享受中。',
                  },
                  {
                    q: '支持哪些设备观看？',
                    a: '蘑菇影视支持手机、平板、电脑和智能电视等多种设备观看，只需打开浏览器即可享受高清观影体验，无需下载任何App。',
                  },
                  {
                    q: '影片资源更新频率如何？',
                    a: '蘑菇影视每天更新海量影视资源，包括最新上映的电影、热播电视剧、人气动漫和综艺节目，确保您不错过任何精彩内容。',
                  },
                ].map((faq, index) => (
                  <details
                    key={index}
                    className="bg-background-100 rounded-xl border border-background-200/70 group"
                  >
                    <summary className="px-5 py-4 flex items-center justify-between cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-600 transition-colors">
                      {faq.q}
                      <div className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section className="mt-14 mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">support@mogutv.com</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-accent-100 text-accent-600">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                  <p className="text-xs text-foreground-500">400-888-8888</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-secondary-100 text-secondary-600">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">北京市朝阳区影视产业园区</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}